import {Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges} from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/internal/operators/map';
import { startWith } from 'rxjs/internal/operators/startWith';
import { LinqNode, LinqsetNode, NODE_NAME_MAX, NODE_NAME_MIN } from '../../common/linqset-model';

@Component({
    selector: 'app-select-node',
    templateUrl: 'select-node.html',
    styleUrl: 'select-node.css',
    standalone: false
})
export class SelectNodeComponent  implements  OnChanges, OnDestroy {
    @Input() nodes: LinqNode[] = [];
    @Input() inputNode:LinqsetNode
    @Input() placeholder:string = "Select a node. Type to filter list."
    @Input() label:string = "Node"
    // search mode finds an existing node: no name checks, and it only reports picking a node or clearing the field
    @Input() search:boolean = false

    nodesControl = new FormControl<string>('', [Validators.required, Validators.minLength(NODE_NAME_MIN), Validators.maxLength(NODE_NAME_MAX), this.createCheckExistingNameValidator()])
    // the options offered: those matching what is typed, from the nodes given (which can arrive, or change, after
    // the field is shown, so the whole list is offered as soon as the field is entered)
    filteredOptions: Observable<LinqNode[]>;
    selectedNode:LinqsetNode
    NODE_NAME_MAX = NODE_NAME_MAX
    NODE_NAME_MIN = NODE_NAME_MIN

    @Output() selectedNodeChange = new EventEmitter<{node:LinqNode, name:string}>();
    // every typed name, with the node it exactly matches (if any); selectedNodeChange skips names that clash
    @Output() nameTyped = new EventEmitter<{name:string, existingNode?:LinqNode}>();

    private readonly nodesChanged = new BehaviorSubject<void>(undefined)
    private readonly valueSub:Subscription

    constructor() {
        this.valueSub = this.nodesControl.valueChanges.subscribe(value => this.valueChanged(value || ''))
        this.filteredOptions = combineLatest([this.nodesControl.valueChanges.pipe(startWith('')), this.nodesChanged]).pipe(
            map(([value]) => this.matching(value || '')),
        );
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['search'] && this.search) {
            this.nodesControl.clearValidators()
            this.nodesControl.updateValueAndValidity({emitEvent:false})
        }
        if (changes['nodes']) this.nodesChanged.next()
        // a new list of nodes shouldn't wipe what someone is searching for
        if (this.search && !changes['inputNode']) return
        this.initialise()
    }

    ngOnDestroy(): void {
        this.valueSub.unsubscribe()
    }

    clear() {
        this.nodesControl.setValue('')
    }

    private initialise() {
        this.nodesControl.reset()

        if (this.inputNode) {
            this.nodesControl.setValue(this.inputNode.name, {emitEvent:false})
        }else {
            this.nodesControl.setValue('', {emitEvent:false})
        }
    }

    // what was typed, or the node picked: tells the listeners
    private valueChanged(value: string|LinqsetNode) {
        this.selectedNode = undefined;
        if (typeof value==='string') {
            const trimmed = value.trim().toLowerCase()
            this.nameTyped.emit({name:value, existingNode:trimmed ? this.nodes?.find(n => n.name?.trim().toLowerCase() === trimmed) : undefined})
        }else {
            this.selectedNode = value
            this.nodesControl.setValue(value.name, {emitEvent:false})
        }
        const typed = typeof value === 'string' ? value : ''
        if (this.search) {
            if (this.selectedNode || typed === '') this.selectedNodeChange.emit({node:this.selectedNode, name:this.nodesControl.value})
        }else if (this.nodesControl.valid || this.selectedNode) this.selectedNodeChange.emit({node:this.selectedNode, name:this.nodesControl.value});
    }

    private matching(value: string|LinqsetNode): LinqNode[] {
        const filterValue = (typeof value === 'string' ? value : value.name).toLowerCase()
        return (this.nodes || []).filter(option => option.name && option.name.toLowerCase().includes(filterValue));
    }

    getNameError() {
        return `Name is required and must be less than ${NODE_NAME_MAX} characters`
    }

    createCheckExistingNameValidator(): ValidatorFn {
        return (control:AbstractControl) : ValidationErrors | null => {
            if (this.selectedNode) return null;

            if (!control.value || typeof control.value !== 'string') {
                return null;
            }

            const value = control.value.trim()

            const existing = this.nodes && this.nodes.find(n => n.name && n.name.toLowerCase() === value.toLowerCase())

            return existing ? {noExistingName:true}: null;
        }
    }
}
